import React, { useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import JSONConfigEditor from './JSONConfigEditor';

/**
 * Step 2: Connection Configuration
 * - JSON editor for connection_config
 * - Config type selector
 * - Example templates
 */
const MCPServerFormStep2 = ({ formData, updateFormData }) => {
  const [configType, setConfigType] = useState(formData.connectionConfig?.type || 'stdio');

  const configTemplates = {
    stdio: {
      type: 'stdio',
      command: 'node',
      args: ['path/to/server.js'],
      env: {}
    },
    http: {
      type: 'http',
      url: 'http://localhost:3000',
      headers: {},
      timeout: 5000
    },
    websocket: {
      type: 'websocket',
      url: 'ws://localhost:3000',
      reconnect: true,
      reconnectInterval: 5000
    }
  };

  const handleConfigChange = (newConfig) => {
    updateFormData({
      connectionConfig: newConfig
    });
  };

  const loadTemplate = (type) => {
    updateFormData({
      connectionConfig: configTemplates[type]
    });
    setConfigType(type);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Connection Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Configure how clients will connect to your MCP server.
        </p>
      </div>

      {/* Config Type Selector */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
          Connection Type <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.keys(configTemplates).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => loadTemplate(type)}
              className={`
                px-4 py-3 rounded-lg border-2 font-medium text-left transition-all
                ${
                  configType === type
                    ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-spooky-bg-medium text-gray-700 dark:text-gray-300 hover:border-cyan-300'
                }
              `}
            >
              <div className="font-bold capitalize">{type}</div>
              <div className="text-xs mt-1 opacity-80">
                {type === 'stdio' && 'Process-based connection'}
                {type === 'http' && 'HTTP REST API'}
                {type === 'websocket' && 'WebSocket connection'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* JSON Editor */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
          Configuration JSON <span className="text-red-500">*</span>
        </label>
        <JSONConfigEditor
          value={formData.connectionConfig}
          onChange={handleConfigChange}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          <FaInfoCircle className="inline mr-1" />
          This configuration tells clients how to connect to your server
        </p>
      </div>

      {/* Configuration Examples */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">
          Configuration Guidelines
        </h3>
        <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
          <li>• <strong>stdio</strong>: For Node.js, Python, or other process-based servers</li>
          <li>• <strong>http</strong>: For REST API endpoints</li>
          <li>• <strong>websocket</strong>: For real-time bidirectional communication</li>
          <li>• Include all required fields for the selected connection type</li>
          <li>• Use environment variables (e.g., $API_KEY) for sensitive data</li>
        </ul>
      </div>

      {/* Validation Info */}
      {formData.connectionConfig && !formData.connectionConfig.type && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-400">
            Configuration must include a "type" field (stdio, http, or websocket)
          </p>
        </div>
      )}
    </div>
  );
};

export default MCPServerFormStep2;
